import {
  workbookSamplingSchema,
  type WorkbookDefinition,
  type WorkbookSampling,
  type WorkbookSourceColumn,
} from '@erp/shared-types';
import { suggestSamplingKeys, type WorkbookPlan } from '@erp/workbooks';
import { stableSerialize } from '@erp/shared-utils';

/** A Sampling key to choose: a suggestion, or the one the Workbook has now. */
export interface SamplingKeyChoice {
  /** Stable across renders: the key's columns, serialized. */
  id: string;
  name: string;
  keyColumns: WorkbookSourceColumn[];
  /** The names of the Sources it samples, in order. */
  sourceNames: string[];
}

/** A Sampling key's identity: its columns, serialized. */
export function samplingKeyId(
  keyColumns: readonly WorkbookSourceColumn[],
): string {
  return stableSerialize(
    keyColumns.map((column) => [column.sourceId, column.column.toLowerCase()]),
  );
}

/**
 * The keys a Workbook can be sampled by, best first: the one it has now,
 * then each key its lookups suggest (see `suggestSamplingKeys`).
 */
export function samplingKeyChoices(
  definition: WorkbookDefinition,
  plan: WorkbookPlan,
): SamplingKeyChoice[] {
  const sourceName = (sourceId: string) =>
    definition.sources.find((source) => source.id === sourceId)?.name ?? '';
  const toChoice = (
    name: string,
    keyColumns: WorkbookSourceColumn[],
  ): SamplingKeyChoice => ({
    id: samplingKeyId(keyColumns),
    name,
    keyColumns,
    sourceNames: keyColumns.map((column) => sourceName(column.sourceId)),
  });
  const current = definition.sampling;
  const choices = suggestSamplingKeys(definition, plan).map((suggestion) =>
    toChoice(suggestion.name, suggestion.keyColumns),
  );
  if (!current) return choices;
  const id = samplingKeyId(current.keyColumns);
  const [first] = current.keyColumns;
  return [
    toChoice(first?.column ?? '', current.keyColumns),
    ...choices.filter((choice) => choice.id !== id),
  ];
}

/** The Sampling a new key starts with: the Workbook's settings kept, or the defaults. */
export function samplingFor(
  keyColumns: WorkbookSourceColumn[],
  current: WorkbookSampling | undefined,
): WorkbookSampling {
  const keySources = new Set(keyColumns.map((column) => column.sourceId));
  return workbookSamplingSchema.parse({
    ...current,
    keyColumns,
    // A stratum must be read from a Source the key samples.
    strata: (current?.strata ?? []).filter((stratum) =>
      keySources.has(stratum.sourceId),
    ),
  });
}

/** The columns a declared stratum can be: every known column of each Source the key samples. */
export function stratumChoices(
  definition: WorkbookDefinition,
  plan: WorkbookPlan,
  sampling: WorkbookSampling,
): (WorkbookSourceColumn & { label: string })[] {
  return sampling.keyColumns.flatMap(({ sourceId, column: key }) => {
    const source = definition.sources.find((item) => item.id === sourceId);
    return (plan.tables.get(sourceId) ?? [])
      .filter((field) => field.name.toLowerCase() !== key.toLowerCase())
      .map((field) => ({
        sourceId,
        column: field.name,
        label: `${source?.name ?? ''} · ${field.name}`,
      }));
  });
}

/** The most declared strata a Sampling holds (`workbookSamplingSchema`). */
const MAX_DECLARED_STRATA = 10;

function sameColumn(a: WorkbookSourceColumn, b: WorkbookSourceColumn) {
  return a.sourceId === b.sourceId && a.column === b.column;
}

/** Whether `column` is one of the Sampling's declared strata. */
export function hasStratum(
  sampling: WorkbookSampling,
  column: WorkbookSourceColumn,
): boolean {
  return sampling.strata.some((item) => sameColumn(item, column));
}

/** Whether another declared stratum fits. */
export function canAddStratum(sampling: WorkbookSampling): boolean {
  return sampling.strata.length < MAX_DECLARED_STRATA;
}

/** The Sampling with `column` a declared stratum (`on`) or not. */
export function withStratum(
  sampling: WorkbookSampling,
  column: WorkbookSourceColumn,
  on: boolean,
): WorkbookSampling {
  const others = sampling.strata.filter((item) => !sameColumn(item, column));
  const strata =
    on && canAddStratum({ ...sampling, strata: others })
      ? [...others, { sourceId: column.sourceId, column: column.column }]
      : others;
  return { ...sampling, strata };
}
